import type { EligibleResourceType, Policy } from './policies/interfaces';
import type {
  CourseTypes,
  DurationTypes,
  ModalityTypes,
  PriceTypes,
} from '@/models/catalog/course';
import type { PeriodTypes } from '@/models/catalog/offer';
import type { ResourceType, ResourceTypesLabels } from '@/models/resources';
import type { RefundStatus } from '@/models/subsidy/status';
import type { PaymentReceiptsTypes } from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/steps/invoice-form/interfaces';

export enum SupplierType {
  NaturalPerson = 'natural_person',
  LegalPerson = 'legal_person',
  InternationalSupplier = 'international_supplier',
}

export interface ResourceApproval {
  uuid: string;
  sale_price: string;
  requested_refundable_amount: string;
  requested_at: string;
  decided_at: null;
  decision_comment: null;
  name: string;
  status: RefundStatus;
  sale_type: PriceTypes;
  request_reason: string;
  category: ResourceTypesLabels;
  type: ResourceType;
  policies: Policy[];
  resource: {
    course: {
      source: EligibleResourceType;
      id: number;
      offer_identifier: string;
      institution_name: string;
      duration: number;
      duration_type: DurationTypes;
      modality: ModalityTypes;
      course_type: CourseTypes;
      workload: string;
      period: PeriodTypes;
      link: null;
      address: {
        city: string;
        state: string;
        campus: string;
      };
    };
  };
  refund_request: RefundItem;
  policy_refund_limit: string;
}

export interface RefundableRequestByResource {
  refund_flow?: boolean;
  policy?: { uuid: string };
  resource: {
    type: string;
    amount: number;
    sale_type: string;
    name?: string;
    request_reason?: string | null;
    link?: string | null;
    data?: {
      course: {
        id?: number;
        modality?: string;
        source?: string;
        course_type?: string;
        duration?: number;
        duration_type?: string;
        period?: string;
        institution_name?: string;
        offer_identifier?: string | null;
        workload?: number | null;
        address?: {
          campus?: string | null;
          city?: string | null;
          state?: string | null;
        };
      };
    };
  };
}

export type RefundEntitiesUuids = {
  refundRequestUuid?: string;
  resourceApprovalUuid: string;
};

export type RefundItem = {
  uuid: string;
  supplier_name: string;
  supplier_document: string;
  supplier_type: SupplierType;
  international_supplier: boolean;
  decision: string;
  status: RefundStatus;
  payment_amount: string;
  purchase_amount: string;
  refundable_amount: string;
  payment_date: string;
  purchase_date: string;
  receiver_fiscal_document: string;
  decision_comment: string;
  decided_at: string;
  created_at: string;
  banking_info: {
    bank: string;
    agency: string;
    pix_key: string;
    pix_type: string;
    holder_name: string;
    account_number: string;
  };
  reference_month: number | null;
  receipts: {
    type: PaymentReceiptsTypes;
    attachments: {
      url: string;
      content: {
        filename: string;
        content_type: string;
        byte_size: number;
        blob: Blob;
      };
    }[];
  }[];
  refund: {
    paid_amount: string;
    paid_at: string;
  };
};

export enum HistoryDecisionType {
  Reject = 'rejected',
  Canceled = 'canceled',
  RevisionRequires = 'revision_required',
  Approved = 'approved',
}

export interface History {
  message: string;
  decision: HistoryDecisionType;
  approved?: boolean;
  decision_comment?: string;
}
